import React, { useState, useEffect } from 'react';
import * as S from './AdminPage.styles';
import { getUsers, createUser, getUserPermissions, getUserPermission, updateUserPermission, User, UserPermission, CreateUserRequest, UpdatePermissionRequest } from '../../entities/user/api/userApi';
import { getCategories } from '../../entities/category/api/categoryApi';
import { TicketCategory } from '../../entities/ticket/model/types';

const AVAILABLE_SITES = ['hudle', 'tournament'];
const AVAILABLE_PAGES = ['tickets', 'clients', 'categories', 'templates', 'dashboard', 'search', 'settings'];

export const AdminPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [permissions, setPermissions] = useState<UserPermission[]>([]);
  const [categories, setCategories] = useState<TicketCategory[]>([]);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditPermission, setShowEditPermission] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  
  const [userFormData, setUserFormData] = useState({
    email: '',
    username: '',
    password: '',
    first_name: '',
    last_name: '',
  });

  const [permissionFormData, setPermissionFormData] = useState<UpdatePermissionRequest>({
    user_id: 0,
    allowed_sites: [],
    allowed_categories: [],
    allowed_pages: [],
    page_permissions: {},
  });

  useEffect(() => {
    loadUsers();
    loadPermissions();
    loadCategories();
  }, []);

  const loadUsers = async () => {
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Ошибка загрузки пользователей:', error);
      alert('Не удалось загрузить пользователей');
    }
  };

  const loadPermissions = async () => {
    try {
      const data = await getUserPermissions();
      setPermissions(data);
    } catch (error) {
      console.error('Ошибка загрузки прав:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await getCategories();
      setCategories(data);
    } catch (error) {
      console.error('Ошибка загрузки категорий:', error);
    }
  };

  const handleCreateUser = async () => {
    if (!userFormData.email || !userFormData.username || !userFormData.password) {
      alert('Заполните все обязательные поля');
      return;
    }

    setLoading(true);
    try {
      await createUser(userFormData as CreateUserRequest);
      setShowCreateUser(false);
      setUserFormData({ email: '', username: '', password: '', first_name: '', last_name: '' });
      await loadUsers();
    } catch (error: any) {
      console.error('Ошибка создания пользователя:', error);
      alert(error.response?.data?.error || 'Не удалось создать пользователя');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPermission = async (user: User) => {
    setSelectedUser(user);
    try {
      const permission = await getUserPermission(user.id);
      setPermissionFormData({
        user_id: user.id,
        allowed_sites: permission.allowed_sites,
        allowed_categories: permission.allowed_categories,
        allowed_pages: permission.allowed_pages,
        page_permissions: permission.page_permissions,
      });
    } catch {
      setPermissionFormData({
        user_id: user.id,
        allowed_sites: [],
        allowed_categories: [],
        allowed_pages: [],
        page_permissions: {},
      });
    }
    setShowEditPermission(true);
  };

  const handleSavePermission = async () => {
    setLoading(true);
    try {
      await updateUserPermission(permissionFormData);
      setShowEditPermission(false);
      await loadPermissions();
    } catch (error: any) {
      console.error('Ошибка сохранения прав:', error);
      alert(error.response?.data?.error || 'Не удалось сохранить права');
    } finally {
      setLoading(false);
    }
  };

  const toggleSite = (site: string) => {
    setPermissionFormData(prev => ({
      ...prev,
      allowed_sites: prev.allowed_sites.includes(site)
        ? prev.allowed_sites.filter(s => s !== site)
        : [...prev.allowed_sites, site],
    }));
  };

  const toggleCategory = (categoryId: string) => {
    setPermissionFormData(prev => ({
      ...prev,
      allowed_categories: prev.allowed_categories.includes(categoryId)
        ? prev.allowed_categories.filter(c => c !== categoryId)
        : [...prev.allowed_categories, categoryId],
    }));
  };

  const togglePage = (page: string) => {
    setPermissionFormData(prev => ({
      ...prev,
      allowed_pages: prev.allowed_pages.includes(page)
        ? prev.allowed_pages.filter(p => p !== page)
        : [...prev.allowed_pages, page],
    }));
  };

  const togglePagePermission = (page: string) => {
    setPermissionFormData(prev => ({
      ...prev,
      page_permissions: {
        ...prev.page_permissions,
        [page]: !prev.page_permissions[page],
      },
    }));
  };

  const getUserPermissionForUser = (userId: number): UserPermission | undefined => {
    return permissions.find(p => p.user_id === userId);
  };

  return (
    <S.AdminContainer>
      <S.AdminHeader>
        <S.AdminTitle>Панель администратора</S.AdminTitle>
        <S.AddButton onClick={() => {
          setUserFormData({ email: '', username: '', password: '', first_name: '', last_name: '' });
          setShowCreateUser(true);
        }}>
          + Создать пользователя
        </S.AddButton>
      </S.AdminHeader>

      <S.AdminContent>
        <S.SectionHeader>
          <S.SectionTitle>Пользователи и права доступа</S.SectionTitle>
        </S.SectionHeader>

        <S.UsersList>
          {users.map(user => {
            const permission = getUserPermissionForUser(user.id);
            return (
              <S.UserCard key={user.id}>
                <S.UserCardHeader>
                  <div>
                    <S.UserCardName>
                      {user.first_name} {user.last_name} ({user.username})
                    </S.UserCardName>
                    <S.UserCardEmail>{user.email}</S.UserCardEmail>
                  </div>
                  {user.is_superuser && <S.SuperUserBadge>Superuser</S.SuperUserBadge>}
                </S.UserCardHeader>
                {permission && (
                  <S.PermissionInfo>
                    <div>Сайты: {permission.allowed_sites.join(', ') || 'нет'}</div>
                    <div>Категории: {permission.allowed_categories.length}</div>
                    <div>Страницы: {permission.allowed_pages.join(', ') || 'нет'}</div>
                  </S.PermissionInfo>
                )}
                {!user.is_superuser && (
                  <S.UserCardActions>
                    <S.EditButton onClick={() => handleEditPermission(user)}>
                      {permission ? 'Изменить права' : 'Назначить права'}
                    </S.EditButton>
                  </S.UserCardActions>
                )}
              </S.UserCard>
            );
          })}
        </S.UsersList>
      </S.AdminContent>

      {showCreateUser && (
        <S.ModalOverlay onClick={() => setShowCreateUser(false)}>
          <S.ModalContent onClick={(e) => e.stopPropagation()}>
            <S.ModalHeader>
              <S.ModalTitle>Создать пользователя</S.ModalTitle>
              <S.CloseButton onClick={() => setShowCreateUser(false)}>×</S.CloseButton>
            </S.ModalHeader>
            <S.ModalBody>
              <S.FormGroup>
                <S.FormLabel>Email *</S.FormLabel>
                <S.FormInput
                  type="email"
                  value={userFormData.email}
                  onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                  placeholder="email@example.com"
                />
              </S.FormGroup>
              <S.FormGroup>
                <S.FormLabel>Username *</S.FormLabel>
                <S.FormInput
                  type="text"
                  value={userFormData.username}
                  onChange={(e) => setUserFormData({ ...userFormData, username: e.target.value })}
                  placeholder="username"
                />
              </S.FormGroup>
              <S.FormGroup>
                <S.FormLabel>Пароль *</S.FormLabel>
                <S.FormInput
                  type="password"
                  value={userFormData.password}
                  onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                  placeholder="Пароль"
                />
              </S.FormGroup>
              <S.FormGroup>
                <S.FormLabel>Имя</S.FormLabel>
                <S.FormInput
                  type="text"
                  value={userFormData.first_name}
                  onChange={(e) => setUserFormData({ ...userFormData, first_name: e.target.value })}
                  placeholder="Имя"
                />
              </S.FormGroup>
              <S.FormGroup>
                <S.FormLabel>Фамилия</S.FormLabel>
                <S.FormInput
                  type="text"
                  value={userFormData.last_name}
                  onChange={(e) => setUserFormData({ ...userFormData, last_name: e.target.value })}
                  placeholder="Фамилия"
                />
              </S.FormGroup>
            </S.ModalBody>
            <S.ModalFooter>
              <S.CancelButton onClick={() => setShowCreateUser(false)}>Отмена</S.CancelButton>
              <S.SaveButton onClick={handleCreateUser} disabled={loading}>
                {loading ? 'Создание...' : 'Создать'}
              </S.SaveButton>
            </S.ModalFooter>
          </S.ModalContent>
        </S.ModalOverlay>
      )}

      {showEditPermission && selectedUser && (
        <S.ModalOverlay onClick={() => setShowEditPermission(false)}>
          <S.ModalContentLarge onClick={(e) => e.stopPropagation()}>
            <S.ModalHeader>
              <S.ModalTitle>Права доступа: {selectedUser.username}</S.ModalTitle>
              <S.CloseButton onClick={() => setShowEditPermission(false)}>×</S.CloseButton>
            </S.ModalHeader>
            <S.ModalBody>
              <S.PermissionSection>
                <S.PermissionSectionTitle>Доступные сайты</S.PermissionSectionTitle>
                <S.CheckboxGroup>
                  {AVAILABLE_SITES.map(site => (
                    <S.CheckboxLabel key={site}>
                      <input
                        type="checkbox"
                        checked={permissionFormData.allowed_sites.includes(site)}
                        onChange={() => toggleSite(site)}
                      />
                      <span>{site === 'hudle' ? 'Hudle' : 'Турнирный сайт'}</span>
                    </S.CheckboxLabel>
                  ))}
                </S.CheckboxGroup>
              </S.PermissionSection>

              <S.PermissionSection>
                <S.PermissionSectionTitle>Доступные категории</S.PermissionSectionTitle>
                <S.CheckboxGroup>
                  {categories.map(category => {
                    const siteName = category.site === 'hudle' ? 'Hudle' : category.site === 'tournament' ? 'Турнамент' : category.site || '';
                    return (
                      <S.CheckboxLabel key={category.id}>
                        <input
                          type="checkbox"
                          checked={permissionFormData.allowed_categories.includes(category.id)}
                          onChange={() => toggleCategory(category.id)}
                        />
                        <span>{category.name} ({siteName})</span>
                      </S.CheckboxLabel>
                    );
                  })}
                </S.CheckboxGroup>
              </S.PermissionSection>

              <S.PermissionSection>
                <S.PermissionSectionTitle>Доступные страницы</S.PermissionSectionTitle>
                <S.CheckboxGroup>
                  {AVAILABLE_PAGES.map(page => (
                    <S.CheckboxLabel key={page}>
                      <input
                        type="checkbox"
                        checked={permissionFormData.allowed_pages.includes(page)}
                        onChange={() => togglePage(page)}
                      />
                      <span>{page}</span>
                    </S.CheckboxLabel>
                  ))}
                </S.CheckboxGroup>
              </S.PermissionSection>

              <S.PermissionSection>
                <S.PermissionSectionTitle>Права на редактирование</S.PermissionSectionTitle>
                <S.PermissionNote>
                  Выберите страницы, на которых пользователь может редактировать данные
                </S.PermissionNote>
                <S.CheckboxGroup>
                  {permissionFormData.allowed_pages.map(page => (
                    <S.CheckboxLabel key={page}>
                      <input
                        type="checkbox"
                        checked={permissionFormData.page_permissions[page] || false}
                        onChange={() => togglePagePermission(page)}
                      />
                      <span>{page}</span>
                    </S.CheckboxLabel>
                  ))}
                </S.CheckboxGroup>
              </S.PermissionSection>
            </S.ModalBody>
            <S.ModalFooter>
              <S.CancelButton onClick={() => setShowEditPermission(false)}>Отмена</S.CancelButton>
              <S.SaveButton onClick={handleSavePermission} disabled={loading}>
                {loading ? 'Сохранение...' : 'Сохранить'}
              </S.SaveButton>
            </S.ModalFooter>
          </S.ModalContentLarge>
        </S.ModalOverlay>
      )}
    </S.AdminContainer>
  );
};
